import React, { useState } from 'react';
import { connect } from 'react-redux';
import {withStyles, Tooltip, IconButton, Typography} from '@material-ui/core';
import { hasPermission, hasAtLeastOnePermission, createHistory, formatDate } from '../../sc-utilities';
import { Redirect } from 'react-router-dom';
import { FindInPage } from '@material-ui/icons';
import ListAltIcon from '@material-ui/icons/ListAlt';
import BillingPlanList from './BillingPlanList';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    "@global": {
        "[class*='chipActive']": {
            color: theme.palette.success.dark
        },
        "[class*='chipPrepared']": {
            color: theme.palette.warning.dark,
        },
        "[class*='chipInactive']": {
            color: theme.palette.error.dark
        }
    },
    chipActive: {
        color: theme.palette.success.dark
    },
    chipPrepared: {
        color: theme.palette.warning.dark,
    },
    chipInactive: {
        color: theme.palette.error.dark
    }
});

const tableRef = React.createRef();

const BillingPlan = props => {
    const { classes, columns, translations, permissions } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, billingPlanID: null, redirectPath: '' });

    let visible = columns && columns['BillingPlan'];

    const getUsedFor = (PrepaidAllowed, PostpaidAllowed) => {
        return PrepaidAllowed && PostpaidAllowed ? translations['both'] || 'Both' : PrepaidAllowed && !PostpaidAllowed ? translations['prepaid_only'] || 'Prepaid only' : PostpaidAllowed && !PrepaidAllowed ? translations['postpaid_only'] || 'Postpaid only' : translations['none'] || 'None'
    };

    let tableColumns = [
        {
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: rowData => <>
                {hasPermission(permissions, 'plan_load') ?
                    <Tooltip key={`Edit-${rowData.PlanID}`} title={translations["billing_plan_details"] || "Billing plan details"}>
                        <IconButton
                            id={`edit_billing_plan_${rowData.PlanID}`}
                            component='div'
                            onClick={(event) => redirectHandler(`/billing/plans/edit?VersionID=${rowData?.VersionID}`)}
                        >
                            <FindInPage />
                        </IconButton>
                    </Tooltip>
                    : null
                }
                {hasPermission(permissions, "plan_versions_list") &&
                <Tooltip key={`View-versions-${rowData.PlanID}`} title={translations["list_versions"] || "List versions"}>
                    <IconButton
                        id={`view_billing_plan_versions_${rowData.PlanID}`}
                        component='div'
                        onClick={(event) => redirectHandler(`/billing/plans/versions?PlanID=${rowData?.PlanID}&PlanName=${rowData.Name}`)}
                    >
                        <ListAltIcon />
                    </IconButton>
                </Tooltip>
                }
            </>
        },
        {
            title: translations["name"] || 'Name', field: 'Name',
            hidden: visible ? visible['Name'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["used_for"] || 'Used for', field: 'UsedFor',
            hidden: visible ? visible['UsedFor'] : false,
            lookup: { 'both': translations['both'] || 'Both', 'prepaid_only': translations['prepaid_only'] || 'Prepaid only', 'postpaid_only': translations['postpaid_only'] || 'Postpaid only', 'none': translations['none'] || 'None' },
            defaultFilter: ['both', 'prepaid_only', 'postpaid_only', 'none'],
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{getUsedFor(rowData.PrepaidAllowed, rowData.PostpaidAllowed)}</Typography>;
            }
        },
        {
            title: translations["description"] || 'Description', field: 'Description',
            hidden: visible ? visible['Description'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Description ? rowData.Description : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["valid_from"] || 'Valid from', field: 'ValidFrom',
            hidden: visible ? visible['ValidFrom'] : false,
            type: 'date',
            cellStyle: { minWidth: 150, maxWidth: 150 },
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.ValidFrom ? formatDate(rowData.ValidFrom) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["valid_to"] || 'Valid to', field: 'ValidTo',
            hidden: visible ? visible['ValidTo'] : false,
            type: 'date',
            cellStyle: { minWidth: 150, maxWidth: 150 },
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.ValidTo ? formatDate(rowData.ValidTo) : translations['date_not_set'] || 'Expiration date not set'}</Typography>;
            }
        }
    ];

    if (!hasAtLeastOnePermission(permissions, [
        "plan_load"
    ])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, billingPlanID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, billingPlanID: billingPlanID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'plan_list') ?
                <BillingPlanList
                    tableColumns={tableColumns}
                    redirectHandler={redirectHandler}
                    tableRef={tableRef}
                /> : null}
        </div>
    );
};

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(BillingPlan));